/**
 * Sparkline — a dependency-free micro-chart for showing KPI trends.
 * Renders an inline SVG polyline with a gradient fill and highlights
 * the last data point. Handles empty / single-value data gracefully.
 *
 * Kept minimal on purpose — this file is the whole implementation, and
 * we deliberately avoid pulling in a charting library for four lines of
 * KPI history per company.
 *
 * Props:
 *   values    number[]  — chronological, oldest → newest.
 *   width     number    — viewBox width; the SVG itself scales to 100% of
 *                         its container so it never forces a min-width
 *   height    number    — px, defaults 28
 *   stroke    string    — CSS color, defaults `currentColor`
 *   invert    bool      — for burn/cost-style metrics, colors lower=better.
 */
export function Sparkline({ values = [], width = 100, height = 28, stroke, invert = false }) {
  const clean = (values || []).map((v) => Number(v) || 0);
  if (clean.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        height={height}
        className="block w-full text-muted-foreground/40"
        aria-hidden
      >
        <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="currentColor" strokeDasharray="2,3" />
      </svg>
    );
  }
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const span = max - min || 1;
  const stepX = clean.length > 1 ? width / (clean.length - 1) : 0;
  const pad = 2;
  const usableH = height - pad * 2;

  const points = clean.map((v, i) => {
    const x = clean.length > 1 ? i * stepX : width / 2;
    const y = pad + usableH - ((v - min) / span) * usableH;
    return [x, y];
  });

  const polyline = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `M0,${height} L${polyline.split(" ").map((p) => p).join(" L")} L${width},${height} Z`;

  const first = clean[0];
  const last = clean[clean.length - 1];
  const trendUp = last >= first;
  const good = invert ? !trendUp : trendUp;
  const color = stroke || (clean.length === 1 ? "currentColor" : (good ? "rgb(34 197 94)" : "rgb(239 68 68)"));
  const [lx, ly] = points[points.length - 1];

  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      height={height}
      className="block w-full overflow-visible"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {clean.length > 1 && <path d={areaPath} fill={`url(#${gradId})`} />}
      {clean.length > 1 && (
        <polyline points={polyline} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      )}
      <circle cx={lx} cy={ly} r={2.5} fill={color} />
    </svg>
  );
}

export default Sparkline;
